import { useState, useEffect, useRef } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Conversation {
  id: string
  title: string
  mode: string
  model: string
  project_slug: string | null
  created_at: string
  updated_at: string
}

interface Message {
  id: string
  role: string
  content: string
  created_at: string
}

export function ChatHistoryPanel() {
  const [projects, setProjects] = useState<string[]>([])
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [replayMode, setReplayMode] = useState(false)

  // Load all conversations and extract unique projects
  useEffect(() => {
    authFetch(apiUrl('/api/history'))
      .then(r => r.json())
      .then(data => {
        const convs = Array.isArray(data) ? data : []
        setConversations(convs)
        const uniqueProjects = [...new Set(convs.map((c: Conversation) => c.project_slug).filter(Boolean))] as string[]
        setProjects(uniqueProjects)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  // Load messages when conversation selected
  const loadConversation = async (conv: Conversation) => {
    setSelectedConv(conv)
    setReplayMode(true)
    const res = await authFetch(apiUrl(`/api/history/${conv.id}`))
    const data = await res.json()
    setMessages(data.messages ?? [])
  }

  const filteredConvs = selectedProject
    ? conversations.filter(c => c.project_slug === selectedProject)
    : conversations

  const formatDate = (ts: string) => {
    try { return new Date(ts).toLocaleDateString() + ' ' + new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }
    catch { return ts }
  }

  // If viewing a conversation's messages
  if (selectedConv && messages.length > 0) {
    const isAQ = selectedConv.id.startsWith('aq-') || selectedConv.title?.startsWith('AQ:')
    return (
      <div style={{ ...styles.root, flexDirection: 'row' }}>
        {/* Left: Chat history panel */}
        <div style={{ width: 380, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={styles.header}>
            <button onClick={() => { setSelectedConv(null); setMessages([]); setReplayMode(false) }} style={styles.backBtn}>← Back</button>
            <span style={styles.headerTitle}>{selectedConv.title}</span>
            <span style={{ ...styles.headerMeta, background: isAQ ? 'rgba(9,114,211,0.1)' : 'rgba(124,110,245,0.1)', color: isAQ ? '#0972d3' : 'var(--accent)' }}>
              {isAQ ? 'AQ Agent' : selectedConv.mode || 'chat'}
            </span>
          </div>
          <div style={{ ...styles.messages, flex: 1 }}>
            {messages.map(m => (
              <div key={m.id} style={{ ...styles.msgBubble, ...(m.role === 'user' ? styles.msgUser : styles.msgAI) }}>
                <div style={styles.msgRole}>{m.role === 'user' ? 'You' : isAQ ? 'AQ Agent' : 'Pulse AI'}</div>
                <div style={styles.msgContent}>{m.role === 'user' ? m.content : renderContent(m.content, selectedConv?.mode)}</div>
                <div style={styles.msgTime}>{formatDate(m.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Right: Live replay demo card with decorations */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)', flexDirection: 'column', gap: 16, overflow: 'hidden', position: 'relative' }}>
          {/* Floating text decorations */}
          <FloatingLabels project={selectedConv.project_slug || 'project'} />
          {/* PULSE watermark - same as landing page top SVG style */}
          <div style={{ zIndex: 2, textAlign: 'center', marginBottom: 8 }}>
            <svg width="300" height="80" viewBox="0 0 450 100">
              <text x="225" y="60" textAnchor="middle" dominantBaseline="middle" fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="900" fontSize="100" letterSpacing="6" fill="none" stroke="rgba(164, 245, 110, 0.07)" strokeWidth="1">PULSE</text>
              <text x="225" y="60" textAnchor="middle" dominantBaseline="middle" fontFamily="ui-sans-serif, system-ui, sans-serif" fontWeight="900" fontSize="100" letterSpacing="6" fill="none" stroke="rgba(255, 208, 134, 0.4)" strokeWidth="1.6">PULSE</text>
            </svg>
          </div>
          {/* Replay card */}
          <div style={{ width: 340, height: '55vh', maxHeight: 460, display: 'flex', flexDirection: 'column', background: '#0f1119', borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.4)', zIndex: 2 }}>
            <ReplayPanel messages={messages} conv={selectedConv} isAQ={isAQ} replayMode={replayMode} onReplay={() => { setReplayMode(false); setTimeout(() => setReplayMode(true), 50) }} />
          </div>
          {/* Tagline below card */}
          <div style={{ zIndex: 2, textAlign: 'center', marginTop: 12 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>Your AI conversation history</div>
          </div>
        </div>
      </div>
    )
  }

  // Project list + conversation list
  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <span style={styles.headerTitle}>Chat History</span>
      </div>
      <div style={styles.body}>
        {loading && <div style={styles.empty}>Loading...</div>}

        {/* Project tabs */}
        {!loading && (
          <div style={styles.tabs}>
            <button
              onClick={() => setSelectedProject(null)}
              style={{ ...styles.tab, ...(selectedProject === null ? styles.tabActive : {}) }}
            >All</button>
            {projects.map(p => (
              <button
                key={p}
                onClick={() => setSelectedProject(p)}
                style={{ ...styles.tab, ...(selectedProject === p ? styles.tabActive : {}) }}
              >{p}</button>
            ))}
          </div>
        )}

        {/* Conversation list */}
        {!loading && filteredConvs.length === 0 && (
          <div style={styles.empty}>No conversations yet</div>
        )}
        {filteredConvs.map(c => (
          <div key={c.id} style={styles.convCard} onClick={() => loadConversation(c)}>
            <div style={styles.convHeader}>
              <span style={styles.convTitle}>{c.title}</span>
              <span style={styles.convTime}>{formatDate(c.updated_at)}</span>
            </div>
            <div style={styles.convMeta}>
              {c.project_slug && <span style={styles.convProject}>{c.project_slug}</span>}
              <span style={styles.convMode}>{c.mode}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function renderContent(content: string, mode?: string) {
  // Hide raw ACTION blocks
  let cleaned = content.replace(/ACTION:\s*```json[\s\S]*?```/g, '').replace(/ACTION:\s*\{[\s\S]*?\}\s*$/gm, '')
  // Hide [summary: ...] metadata
  const summaryMatch = cleaned.match(/\[summary: read=(\d+), modified=(\d+), duration=([\d.]+)s\]/)
  cleaned = cleaned.replace(/\[summary:.*?\]/g, '')
  // Hide [Read: ...] [Modified: ...] prefixes
  cleaned = cleaned.replace(/^\[Read:.*?\]\n?/m, '').replace(/^\[Modified:.*?\]\n?/m, '')
  cleaned = cleaned.trim()
  if (!cleaned) return null

  const elements: JSX.Element[] = []
  
  // Split by terminal blocks ($ command\noutput)
  const terminalRegex = /^\$ (.+?)\n([\s\S]*?)(?=^\$ |$(?!\n))/gm
  let lastIdx = 0
  let match: RegExpExecArray | null
  const withNewline = cleaned + '\n'
  
  while ((match = terminalRegex.exec(withNewline)) !== null) {
    // Text before terminal
    const before = withNewline.slice(lastIdx, match.index).trim()
    if (before) elements.push(<span key={`t-${lastIdx}`} style={{ whiteSpace: 'pre-wrap' }}>{renderMarkdown(before)}</span>)
    // Terminal card
    elements.push(
      <div key={`cmd-${match.index}`} style={terminalCardStyle}>
        <div style={terminalHeaderStyle}>shell</div>
        <div style={terminalCmdStyle}><span style={{ color: '#16a34a' }}>$ </span>{match[1]}</div>
        {match[2]?.trim() && <div style={terminalOutputStyle}>{match[2].trim()}</div>}
      </div>
    )
    lastIdx = match.index + match[0].length
  }
  
  // Remaining text
  const remaining = withNewline.slice(lastIdx).trim()
  if (remaining) elements.push(<span key="rest" style={{ whiteSpace: 'pre-wrap' }}>{renderMarkdown(remaining)}</span>)
  
  // Summary badge
  if (summaryMatch) {
    elements.push(
      <div key="summary" style={summaryStyle}>
        {+summaryMatch[1] > 0 && <span>read: {summaryMatch[1]}</span>}
        {+summaryMatch[2] > 0 && <span>modified: {summaryMatch[2]}</span>}
        <span>{summaryMatch[3]}s</span>
      </div>
    )
  }
  
  return elements.length > 0 ? <>{elements}</> : <span style={{ whiteSpace: 'pre-wrap' }}>{cleaned}</span>
}

function renderMarkdown(text: string) {
  // Simple markdown: code blocks, inline code, bold
  const parts = text.split(/(```[\s\S]*?```|`[^`]+`|\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (!part) return null
    if (part.startsWith('```') && part.endsWith('```')) {
      const code = part.replace(/^```\w*\n?/, '').replace(/\n?```$/, '')
      return <pre key={i} style={styles.codeBlock}>{code}</pre>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: 3, fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{part.slice(1, -1)}</code>
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    return <span key={i}>{part}</span>
  })
}

// ── Floating Labels ─ Decorative animated text around replay card ──────

function FloatingLabels({ project }: { project: string }) {
  const [hovered, setHovered] = useState<number | null>(null)

  const labels = [
    { text: `Your conversation with AI`, color: '#7c6ef5', top: '6%', left: '8%', rotate: -3, size: 11, font: 'ui-monospace, monospace', anim: 'float-a' },
    { text: `Updates on ${project}`, color: '#5fd4a4', top: '4%', right: '6%', rotate: 2, size: 13, font: 'system-ui, sans-serif', anim: 'float-b' },
    { text: `"How does auth work?"`, color: '#f5a157', bottom: '14%', left: '4%', rotate: 1, size: 9, font: 'ui-monospace, monospace', anim: 'float-c' },
    { text: `Add dark mode`, color: '#38bdf8', bottom: '6%', right: '6%', rotate: -2, size: 14, font: 'Georgia, serif', anim: 'float-d' },
    { text: `Check the weather endpoint`, color: '#ffd086', top: '20%', left: '2%', rotate: 4, size: 8, font: 'ui-monospace, monospace', anim: 'float-a' },
    { text: `Fix the login bug`, color: '#f07070', top: '16%', right: '2%', rotate: -1, size: 12, font: 'system-ui, sans-serif', anim: 'float-c' },
    { text: `AI-powered`, color: '#7c6ef5', bottom: '24%', right: '10%', rotate: 3, size: 16, font: 'Georgia, serif', anim: 'float-b' },
    { text: `Real-time collab`, color: '#5fd4a4', bottom: '28%', left: '6%', rotate: -2, size: 10, font: 'system-ui, sans-serif', anim: 'float-d' },
    { text: `Deploy to cloud`, color: '#ce2432', top: '35%', left: '1%', rotate: -5, size: 9, font: 'ui-monospace, monospace', anim: 'float-a' },
    { text: `Generate tests`, color: '#38bdf8', top: '30%', right: '1%', rotate: 2, size: 11, font: 'Georgia, serif', anim: 'float-c' },
  ]

  return (
    <>
      <style>{`
        @keyframes float-a { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-8px) scale(1.05)} }
        @keyframes float-b { 0%,100%{transform:translateY(0) scale(1)} 30%{transform:translateY(6px) scale(0.95)} 70%{transform:translateY(-6px) scale(1.08)} }
        @keyframes float-c { 0%,100%{transform:translateX(0) scale(1)} 50%{transform:translateX(8px) scale(1.03)} }
        @keyframes float-d { 0%,100%{transform:scale(1) rotate(0deg)} 25%{transform:scale(1.1) rotate(1deg)} 75%{transform:scale(0.92) rotate(-1deg)} }
      `}</style>
      {labels.map((label, i) => (
        <div
          key={i}
          onMouseEnter={() => setHovered(i)}
          onMouseLeave={() => setHovered(null)}
          style={{
            position: 'absolute',
            top: label.top, bottom: (label as any).bottom, left: label.left, right: (label as any).right,
            padding: '5px 10px',
            borderRadius: 6,
            background: hovered === i ? `${label.color}25` : `${label.color}08`,
            border: `1px solid ${hovered === i ? label.color + '70' : label.color + '18'}`,
            color: label.color,
            fontSize: label.size,
            fontFamily: label.font,
            fontWeight: label.size > 12 ? 800 : 600,
            fontStyle: label.font.includes('Georgia') ? 'italic' : 'normal',
            transform: `rotate(${label.rotate}deg) scale(${hovered === i ? 1.15 : 1})`,
            transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'default',
            animation: `${label.anim} ${2.5 + i * 0.4}s ease-in-out infinite`,
            animationDelay: `${i * 0.25}s`,
            zIndex: 1,
            whiteSpace: 'nowrap',
            opacity: hovered === i ? 1 : 0.6,
            boxShadow: hovered === i ? `0 6px 24px ${label.color}40` : 'none',
            letterSpacing: label.size > 12 ? '0.5px' : '0',
          }}
        >
          {label.text}
        </div>
      ))}
    </>
  )
}

// ── Replay Panel ─ Live typing animation next to chat panel ─────────

function ReplayPanel({ messages, conv, isAQ, replayMode, onReplay }: { messages: Message[]; conv: Conversation; isAQ: boolean; replayMode: boolean; onReplay: () => void }) {
  const [visibleMessages, setVisibleMessages] = useState<Array<{ role: string; content: string }>>([]) 
  const [currentIdx, setCurrentIdx] = useState(0)
  const [currentText, setCurrentText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const startedRef = useRef(false)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [visibleMessages, currentText])

  // Auto-start on mount, restart on replayMode change
  useEffect(() => {
    setVisibleMessages([])
    setCurrentIdx(0)
    setCurrentText('')
    setIsTyping(false)
    setIsDone(false)
    startedRef.current = true
  }, [replayMode, messages])

  useEffect(() => {
    if (!startedRef.current) return
    if (currentIdx >= messages.length) { setIsDone(true); return }

    const msg = messages[currentIdx]
    const content = msg.content || ''

    if (msg.role === 'user') {
      setTimeout(() => {
        setVisibleMessages(prev => [...prev, { role: 'user', content }])
        setCurrentIdx(prev => prev + 1)
      }, 400)
      return
    }

    // AI message — type out
    setIsTyping(true)
    setCurrentText('')
    let charIdx = 0
    const speed = content.length > 500 ? 3 : content.length > 200 ? 10 : 20

    const interval = setInterval(() => {
      charIdx += Math.ceil(Math.random() * 4) + 2
      if (charIdx >= content.length) {
        clearInterval(interval)
        setCurrentText('')
        setIsTyping(false)
        setVisibleMessages(prev => [...prev, { role: 'assistant', content }])
        setTimeout(() => setCurrentIdx(prev => prev + 1), 600)
      } else {
        setCurrentText(content.slice(0, charIdx))
      }
    }, speed)

    return () => clearInterval(interval)
  }, [currentIdx, messages])

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#161825' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: isDone ? '#666' : '#16a34a', animation: isDone ? 'none' : 'replay-blink 1.5s infinite' }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', fontFamily: 'ui-monospace, monospace' }}>Live Replay</span>
        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontFamily: 'ui-monospace, monospace' }}>{Math.min(currentIdx, messages.length)}/{messages.length}</span>
        <div style={{ flex: 1 }} />
        {isDone && <button onClick={onReplay} style={{ padding: '4px 10px', borderRadius: 4, border: '1px solid rgba(95,212,164,0.3)', background: 'rgba(95,212,164,0.08)', color: '#5fd4a4', fontSize: 10, cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>▶ Replay</button>}
      </div>

      {/* Chat replay area */}
      <div style={{ flex: 1, overflow: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visibleMessages.map((m, i) => (
          <div key={i} style={{ padding: '8px 12px', borderRadius: 8, ...(m.role === 'user' ? { background: 'rgba(9,114,211,0.08)', borderLeft: '2px solid #0972d3' } : { background: 'rgba(255,255,255,0.03)', borderLeft: '2px solid #444' }) }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 4, fontFamily: 'ui-monospace, monospace', textTransform: 'uppercase' as const }}>{m.role === 'user' ? 'You' : isAQ ? 'AQ Agent' : 'Pulse AI'}</div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: '#e0e0e0' }}>{m.role === 'user' ? m.content : renderContent(m.content, conv.mode)}</div>
          </div>
        ))}
        {/* Currently typing */}
        {isTyping && currentText && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', borderLeft: '2px solid #444' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', marginBottom: 4, fontFamily: 'ui-monospace, monospace', textTransform: 'uppercase' as const }}>{isAQ ? 'AQ Agent' : 'Pulse AI'}</div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: '#e0e0e0' }}>
              {renderContent(currentText, conv.mode)}
              <span style={{ color: '#0972d3', animation: 'replay-blink 0.8s infinite' }}>▊</span>
            </div>
          </div>
        )}
        {isTyping && !currentText && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.03)', borderLeft: '2px solid #444' }}>
            <span style={{ fontSize: 16, color: '#666', animation: 'replay-dot1 1.4s infinite' }}>•</span>
            <span style={{ fontSize: 16, color: '#666', animation: 'replay-dot2 1.4s infinite' }}>•</span>
            <span style={{ fontSize: 16, color: '#666', animation: 'replay-dot3 1.4s infinite' }}>•</span>
          </div>
        )}
        {isDone && (
          <div style={{ textAlign: 'center', color: '#16a34a', fontSize: 11, fontFamily: 'ui-monospace, monospace', padding: 16, opacity: 0.7 }}>✓ Replay complete</div>
        )}
        <div ref={bottomRef} />
      </div>
      <style>{`
        @keyframes replay-blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes replay-dot1 { 0%,80%,100%{opacity:0.3} 40%{opacity:1} }
        @keyframes replay-dot2 { 0%,80%,100%{opacity:0.3} 50%{opacity:1} }
        @keyframes replay-dot3 { 0%,80%,100%{opacity:0.3} 60%{opacity:1} }
      `}</style>
    </>
  )
}

const terminalCardStyle: React.CSSProperties = {
  borderRadius: 6, overflow: 'hidden', margin: '8px 0', border: '1px solid rgba(255,255,255,0.1)', background: '#0d1117',
}
const terminalHeaderStyle: React.CSSProperties = {
  padding: '4px 10px', background: '#161825', borderBottom: '1px solid rgba(255,255,255,0.1)', fontSize: 10, fontFamily: 'ui-monospace, monospace', color: '#16a34a',
}
const terminalCmdStyle: React.CSSProperties = {
  padding: '6px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#60a5fa',
}
const terminalOutputStyle: React.CSSProperties = {
  padding: '6px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#ccc', whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto', borderTop: '1px solid rgba(255,255,255,0.06)',
}
const summaryStyle: React.CSSProperties = {
  display: 'flex', gap: 12, marginTop: 8, padding: '6px 10px', borderRadius: 4, background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.2)', fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#16a34a',
}

const styles: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-0)' },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px', borderBottom: '1px solid var(--border)' },
  headerTitle: { font: '700 15px/1 system-ui', color: 'var(--text-0)', flex: 1 },
  headerMeta: { font: '10px/1 ui-monospace, monospace', color: 'var(--text-2)', padding: '2px 8px', background: 'rgba(124,110,245,0.1)', borderRadius: 4 },
  backBtn: { background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' },
  body: { flex: 1, overflowY: 'auto', padding: 16 },
  empty: { color: 'var(--text-2)', fontSize: 13, textAlign: 'center', padding: 40 },
  tabs: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' },
  tab: { padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600 },
  tabActive: { background: 'rgba(124,110,245,0.1)', borderColor: 'rgba(124,110,245,0.3)', color: 'var(--accent)' },
  convCard: { padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.2s' },
  convHeader: { display: 'flex', alignItems: 'center', gap: 8 },
  convTitle: { font: '12px/1.3 system-ui', color: 'var(--text-0)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  convTime: { font: '9px/1 ui-monospace, monospace', color: 'var(--text-2)' },
  convMeta: { display: 'flex', gap: 6, marginTop: 6 },
  convProject: { font: '9px/1 ui-monospace, monospace', padding: '2px 6px', borderRadius: 4, background: 'rgba(95,212,164,0.1)', color: 'var(--accent-2)' },
  convMode: { font: '9px/1 ui-monospace, monospace', padding: '2px 6px', borderRadius: 4, background: 'rgba(124,110,245,0.08)', color: 'var(--accent)' },
  messages: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  msgBubble: { padding: '10px 14px', borderRadius: 10, maxWidth: '85%' },
  msgUser: { alignSelf: 'flex-end', background: 'rgba(124,110,245,0.08)', border: '1px solid rgba(124,110,245,0.15)' },
  msgAI: { alignSelf: 'flex-start', background: 'var(--bg-1)', border: '1px solid var(--border)' },
  msgRole: { font: '700 9px/1 system-ui', color: 'var(--text-2)', marginBottom: 4, textTransform: 'uppercase' as const },
  msgContent: { font: '12px/1.5 system-ui', color: 'var(--text-0)' },
  msgTime: { font: '8px/1 ui-monospace, monospace', color: 'var(--text-2)', marginTop: 6, textAlign: 'right' as const },
  codeBlock: { background: 'rgba(0,0,0,0.3)', borderRadius: 6, padding: '8px 12px', fontSize: 10, fontFamily: 'ui-monospace, monospace', overflow: 'auto', margin: '6px 0' },
  diffAdd: { color: '#5fd4a4', background: 'rgba(95,212,164,0.08)', borderLeft: '2px solid #5fd4a4', paddingLeft: 6 },
  diffRm: { color: '#f07070', background: 'rgba(240,112,112,0.08)', borderLeft: '2px solid #f07070', paddingLeft: 6, textDecoration: 'line-through', opacity: 0.8 },
}
